import type { FilmSettings, Ink, ScreenSettings, SepMode, Separation, WhiteSettings } from '@eip/engine';
import { useStore } from '../state/store';
import { canBuildIndex, printWidthFor, type SepDefaults } from '../state/sep';
import { Select, Slider } from './controls';
import { InkList } from './InkList';
import { WhitePanel } from './WhitePanel';
import { ScreenPanel } from './ScreenPanel';
import { FilmPanel } from './FilmPanel';
import { MODE_LABELS } from '../lib/sep-ui';
import { Section } from './Section';

const MODE_OPTIONS = (Object.keys(MODE_LABELS) as SepMode[]).map((value) => ({ value, label: MODE_LABELS[value].label }));

/** The right-hand panel for screen-print mode: mode select, ink list, white,
 *  screening, film and trap settings, plus a status line for the last (or
 *  in-flight) plate build. Mounted instead of `PipelinePanel` when
 *  `appMode === 'sep'`; `InspectorPanel` stays mounted below either way. */
export function SepPanel() {
  const doc = useStore((s) => s.docs.find((d) => d.id === s.activeId));
  const settings = useStore((s) => s.settings);
  const setSeparation = useStore((s) => s.setSeparation);
  const setSepDefaults = useStore((s) => s.setSepDefaults);

  if (!doc) return <div className="section sep-panel"><div className="muted small">Open a file to set up its separation.</div></div>;
  if (!doc.sep) return <div className="section sep-panel"><div className="muted small">Preparing separation…</div></div>;

  const sep = doc.sep;
  const separation = sep.separation;

  /** Every edit goes through `setSeparation`; the shop-wide sections
   *  (everything but inks) also feed back into `sepDefaults` so the next
   *  document opened starts from what was just configured here. */
  const update = (next: Separation, defaultsPatch?: Partial<SepDefaults>) => {
    setSeparation(doc.id, next);
    if (defaultsPatch) setSepDefaults(defaultsPatch);
  };
  const setMode = (mode: SepMode) => update({ ...separation, mode }, { mode });
  const setInks = (inks: Ink[]) => update({ ...separation, inks });
  const setWhite = (white: WhiteSettings) => update({ ...separation, white }, { white });
  const setScreen = (screen: ScreenSettings) => update({ ...separation, screen }, { screen });
  const setFilm = (film: FilmSettings) => update({ ...separation, film }, { film });
  const setTrap = (trapPx: number) => update({ ...separation, trapPx }, { trapPx });

  const rotationWarning = separation.mode === 'index' && doc.run
    ? canBuildIndex(separation, { width: doc.run.width, height: doc.run.height }, printWidthFor(settings, doc.run))
    : null;

  return (
    <div className="sep-panel">
      <Section id="mode" title="Mode">
        <Select label="Mode" value={separation.mode} options={MODE_OPTIONS} onChange={(v) => setMode(v as SepMode)} />
        <div className="muted small">{MODE_LABELS[separation.mode].hint}</div>
      </Section>

      <InkList doc={doc} inks={separation.inks} mode={separation.mode} plates={sep.plates} onChange={setInks} />
      <WhitePanel white={separation.white} onChange={setWhite} />
      <ScreenPanel screen={separation.screen} mode={separation.mode} onChange={setScreen} />
      <FilmPanel film={separation.film} onChange={setFilm} />

      <Section id="trap" title="Trap">
        <Slider label="Trap" hint="Overlap between neighbouring inks so gaps don't show" value={separation.trapPx} min={0} max={10} step={1} unit="px" onChange={setTrap} />
      </Section>

      {rotationWarning && <div className="section muted small" style={{ color: 'var(--warn)' }}>{rotationWarning}</div>}

      {/* The build status is in the panel's pinned footer now (`PanelFooter`),
          with the same `.sep-status` element, classes and `data-*` attributes:
          "did the build land" is what a shop checks after every edit, and it
          used to be the one line below the fold. */}
    </div>
  );
}
